// =============================================================================
// FORM FIELD CARD - Sortable field in the canvas
// =============================================================================

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
  Copy,
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  Upload,
  Image,
  PenTool,
  Star,
  LayoutTemplate,
  Minus,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import type { FormField } from '../types';
import { useFormBuilderStore } from '../store';

const ICON_MAP: Record<string, LucideIcon> = {
  text: Type,
  email: Mail,
  number: Hash,
  phone: Phone,
  textarea: AlignLeft,
  select: ChevronDown,
  multiselect: ListChecks,
  checkbox: CheckSquare,
  radio: CircleDot,
  date: Calendar,
  datetime: Clock,
  file: Upload,
  image: Image,
  signature: PenTool,
  rating: Star,
  section: LayoutTemplate,
  divider: Minus,
};

interface FormFieldCardProps {
  field: FormField;
  sectionId: string;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export function FormFieldCard({ field, sectionId, isSelected, onClick }: FormFieldCardProps) {
  const { removeField, duplicateField } = useFormBuilderStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = ICON_MAP[field.type] || Type;
  const isRequired = field.validation.some((v) => v.type === 'required');

  // Render field preview based on type
  const renderFieldPreview = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'phone':
        return (
          <Input
            placeholder={field.placeholder}
            disabled
            className="bg-secondary-50 dark:bg-secondary-900"
          />
        );
      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder}
            disabled
            rows={3}
            className="bg-secondary-50 dark:bg-secondary-900"
          />
        );
      case 'select':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">{field.placeholder || 'Select...'}</span>
            <ChevronDown className="h-4 w-4 text-secondary-400" />
          </div>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border border-secondary-300 dark:border-secondary-600" />
            <span className="text-sm text-secondary-600 dark:text-secondary-400">
              {field.label}
            </span>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.slice(0, 3).map((option, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border border-secondary-300 dark:border-secondary-600" />
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        );
      case 'date':
      case 'datetime':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">Select date...</span>
            <Calendar className="h-4 w-4 text-secondary-400" />
          </div>
        );
      case 'file':
      case 'image':
        return (
          <div className="h-24 rounded-md border-2 border-dashed border-secondary-200 bg-secondary-50 flex items-center justify-center dark:border-secondary-700 dark:bg-secondary-900">
            <div className="text-center">
              <Upload className="h-6 w-6 text-secondary-400 mx-auto" />
              <span className="text-sm text-secondary-500 mt-1">
                Click to upload
              </span>
            </div>
          </div>
        );
      case 'rating':
        return (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="h-6 w-6 text-secondary-300 dark:text-secondary-600" />
            ))}
          </div>
        );
      case 'divider':
        return <div className="h-px bg-secondary-200 dark:bg-secondary-700 my-2" />;
      default:
        return null;
    }
  };

  // Layout fields don't show label
  if (field.type === 'divider') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'rounded-lg border p-3 transition-all',
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500/20'
            : 'border-secondary-200 dark:border-secondary-700',
          isDragging && 'opacity-50'
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-secondary-400" />
          </button>
          <div className="flex-1">{renderFieldPreview()}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border bg-white p-4 transition-all',
        'dark:bg-secondary-800',
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-500/20'
          : 'border-secondary-200 dark:border-secondary-700',
        isDragging && 'opacity-50 shadow-lg'
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          className="cursor-grab touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-secondary-400" />
        </button>
        <Icon className="h-4 w-4 text-secondary-500" />
        <span className="font-medium text-secondary-900 dark:text-white">
          {field.label}
        </span>
        {isRequired && (
          <Badge variant="destructive" className="text-[10px] px-1 py-0">
            Required
          </Badge>
        )}
        <div className="flex-1" />
        {isSelected && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                duplicateField(sectionId, field.id);
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                removeField(sectionId, field.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>

      {/* Field preview */}
      <div>{renderFieldPreview()}</div>

      {/* Help text */}
      {field.helpText && (
        <p className="mt-2 text-xs text-secondary-500">{field.helpText}</p>
      )}
    </div>
  );
}
